import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../api';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  MapPin, Star, Clock, Users, Phone, Mail, Heart, 
  CalendarDays, Check, Loader2, ChevronLeft,
  Ticket, AlertCircle, Bell, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import ClassBookingDialog from '../components/ClassBookingDialog';
import ClassPassPurchaseDialog from '../components/ClassPassPurchaseDialog';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';
import { formatStudioAddress, googleMapsDirectionsUrl } from '../lib/studioAddress';

export const StudioDetailPage = () => {
  const { slug } = useParams();
  const { user, api, isAuthenticated, toggleFavouriteStudio } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Bug #6 fix: honour ?tab=<value> so callers (e.g. the schedule booking
  // dialog's "Browse this studio's ClassPasses" CTA) can deep-link into a
  // specific tab. Falls back to "about" like the original defaultValue.
  const initialTab = ['about', 'schedule', 'classpasses', 'instructors', 'reviews']
    .includes(searchParams.get('tab')) ? searchParams.get('tab') : 'about';
  
  const [studio, setStudio] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classPasses, setClassPasses] = useState([]);
  const [userClassPasses, setUserClassPasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookingClass, setBookingClass] = useState(null);
  const [purchasePass, setPurchasePass] = useState(null);  // shared <ClassPassPurchaseDialog />
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [studioCanAcceptPayments, setStudioCanAcceptPayments] = useState(true);
  const [paymentStatusMessage, setPaymentStatusMessage] = useState('');

  const fetchStudioPaymentStatus = useCallback(async () => {
    if (!studio?.id) return;
    try {
      const data = await apiGet('/api/studios/{studio_id}/payment-status', { params: { studio_id: studio.id } });
      setStudioCanAcceptPayments(data.can_accept_payments);
      setPaymentStatusMessage(data.message || '');
    } catch (e) {
      console.error('Failed to fetch payment status:', e);
    }
  }, [studio?.id]);

  const fetchStudioData = useCallback(async () => {
    try {
      // The studio fetch is the only blocking call — if this 404s the page
      // genuinely can't render. Enrichment calls below run in parallel and
      // failures are swallowed (Iter-46an: previously a 500 from any one of
      // them killed the whole page with a misleading "Studio not found").
      const studioRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/studios/${slug}`);
      if (!studioRes.ok) throw new Error('Studio not found');
      const studioData = await studioRes.json();
      setStudio(studioData);

      const safeFetch = async (url, fallback) => {
        try {
          const r = await fetch(url);
          if (!r.ok) return fallback;
          return await r.json();
        } catch {
          return fallback;
        }
      };

      const [instructorsData, passesData, reviewsData, paymentStatus] = await Promise.all([
        safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/instructors?studio_id=${studioData.id}`, []),
        safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/class-passes?studio_id=${studioData.id}`, []),
        safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/studio/${studioData.id}`, { reviews: [] }),
        safeFetch(`${process.env.REACT_APP_BACKEND_URL}/api/studios/${studioData.id}/payment-status`, { can_accept_payments: true, message: '' }),
      ]);

      setInstructors(Array.isArray(instructorsData) ? instructorsData : []);
      setClassPasses(Array.isArray(passesData) ? passesData : []);
      // Iter-54 fix: previously `reviewsData?.reviews || reviewsData || []`
      // returned an empty array `[]` when the server sent
      // `{reviews:[], average_rating:0, total_reviews:0}` — because empty
      // arrays are truthy so the `||` fallback skipped the object entirely.
      // That collapsed state into an array shape, `reviews.reviews` became
      // undefined, and the Reviews tab silently swallowed all existing rows
      // until a submit re-fetch overwrote state with the correct object.
      // Normalise to the expected `{reviews, average_rating, total_reviews}`
      // shape regardless of what the server / safeFetch returns.
      setReviews({
        reviews: Array.isArray(reviewsData?.reviews) ? reviewsData.reviews : [],
        average_rating: reviewsData?.average_rating || 0,
        total_reviews: reviewsData?.total_reviews || 0,
      });
      setStudioCanAcceptPayments(paymentStatus.can_accept_payments !== false);
      setPaymentStatusMessage(paymentStatus.message || '');
    } catch (e) {
      console.error('Failed to fetch studio:', e);
      toast.error('Studio not found');
      navigate('/studios');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  const fetchClasses = useCallback(async () => {
    if (!studio?.id) return;
    try {
      // Iter-46an: fetch the WHOLE WEEK around the selected date so the
      // right-hand panel can show a per-day grouping for the week the user
      // tapped on, not just that single day.
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
      const startStr = format(weekStart, 'yyyy-MM-dd');
      const endStr = format(weekEnd, 'yyyy-MM-dd');
      // Iter-70: use apiGet (with credentials) so backend can compute
      // `is_booked` per class for the current client — enables the
      // "Already Booked" state below (blocks accidental double-booking).
      const data = await apiGet('/api/classes/schedule', {
        query: { studio_id: studio.id, start_date: startStr, end_date: endStr },
      });
      // Flatten classes_by_date → single array sorted by start_time so the
      // existing render logic (groupedClasses below) does the per-day split.
      const flat = [];
      Object.values(data.classes_by_date || {}).forEach((arr) => {
        if (Array.isArray(arr)) flat.push(...arr);
      });
      flat.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setClasses(flat);
    } catch (e) {
      console.error('Failed to fetch classes:', e);
    }
  }, [studio?.id, selectedDate]);

  const fetchUserClassPasses = useCallback(async () => {
    if (!studio?.id) return;
    try {
      const data = await apiGet('/api/user-class-passes', { query: { studio_id: studio.id, status: 'active' } });
      setUserClassPasses(data);
    } catch (e) {
      console.error('Failed to fetch user class passes:', e);
    }
  }, [studio?.id]);

  useEffect(() => {
    fetchStudioData();
  }, [fetchStudioData]);

  useEffect(() => {
    if (studio) {
      fetchClasses();
    }
  }, [studio, fetchClasses]);

  useEffect(() => {
    if (isAuthenticated && studio) {
      fetchUserClassPasses();
      fetchSavedCards();
      fetchStudioPaymentStatus();
    }
  }, [isAuthenticated, studio, fetchUserClassPasses, fetchStudioPaymentStatus]);

  const fetchSavedCards = async () => {
    try {
      const data = await apiGet('/api/payment-methods');
      setSavedCards(data || []);
    } catch (e) {
      console.error('Failed to fetch saved cards:', e);
    }
  };

  const handleBookClass = (cls) => {
    if (!isAuthenticated) {
      toast.error('Please login to book a class');
      navigate('/login', { state: { from: `/studio/${slug}` } });
      return;
    }
    // Open the shared booking dialog — it handles ClassPass selection,
    // RyftPay 3DS card-form, and the strict /api/bookings security guard.
    setBookingClass(cls);
  };

  const handleBooked = () => {
    setBookingClass(null);
    fetchClasses();
    fetchUserClassPasses();
  };

  const handleJoinWaitlist = async (classItem) => {
    if (!isAuthenticated) {
      toast.error('Please login to join waitlist');
      navigate('/login', { state: { from: `/studio/${slug}` } });
      return;
    }
    
    try {
      const data = await apiPost('/api/waitlist', { body: {
        class_id: classItem.id,
        studio_id: studio.id
      } });
      // Iter-46am: include the grace window so the user knows their priority duration.
      const mins = data?.waitlist_grace_minutes ?? 60;
      const formatGrace = (m) => {
        if (m <= 0) return null;
        if (m < 60) return `${m} minutes`;
        if (m === 60) return '1 hour';
        return m % 60 === 0 ? `${m / 60} hours` : `${Math.floor(m / 60)}h ${m % 60}m`;
      };
      const graceText = formatGrace(mins);
      toast.success(
        graceText
          ? `Added to waitlist (position ${data.position}). If a seat opens up you'll have ${graceText} priority to book it.`
          : `Added to waitlist (position ${data.position}). If a seat opens up everyone is notified at once — first to book wins.`,
        { duration: 7000 }
      );
      fetchClasses();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to join waitlist'));
    }
  };

  const handleFavourite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save favourites');
      return;
    }
    try {
      await toggleFavouriteStudio(studio.id);
      toast.success('Favourites updated');
    } catch (e) {
      toast.error('Failed to update favourites');
    }
  };

  const isFavourite = user?.favourite_studios?.includes(studio?.id);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit a review');
      return;
    }
    setReviewLoading(true);
    try {
      await apiPost('/api/reviews', { body: {
        studio_id: studio.id,
        rating: reviewRating,
        comment: reviewComment
      } });
      toast.success('Review submitted!');
      setShowReviewDialog(false);
      setReviewComment('');
      setReviewRating(5);
      // Iter-54: same defensive shape normalisation as the initial fetch
      // above — never let state degrade into an array or a partial object.
      const reviewsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/studio/${studio.id}`);
      const fresh = await reviewsRes.json();
      setReviews({
        reviews: Array.isArray(fresh?.reviews) ? fresh.reviews : [],
        average_rating: fresh?.average_rating || 0,
        total_reviews: fresh?.total_reviews || 0,
      });
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to submit review'));
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!studio) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="relative h-80 md:h-96">
        <img 
          src={studio.cover_image_url || 'https://images.unsplash.com/photo-1761971975962-9cc397e2ba2a?w=1920&q=80'}
          alt={studio.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-7xl mx-auto">
            <Button 
              variant="ghost" 
              className="text-white mb-4 hover:bg-white/20"
              onClick={() => navigate('/studios')}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Studios
            </Button>
            
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-primary">{studio.category || 'Fitness'}</Badge>
                  <button 
                    onClick={() => isAuthenticated && setShowReviewDialog(true)}
                    className="flex items-center gap-1 text-amber-400 hover:text-amber-300 transition-colors"
                    title={isAuthenticated ? "Write a review" : "Login to review"}
                  >
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-white">{reviews.average_rating || studio.rating || 'New'}</span>
                    {reviews.total_reviews > 0 && (
                      <span className="text-white/70 text-sm">({reviews.total_reviews})</span>
                    )}
                  </button>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{studio.name}</h1>
                {formatStudioAddress(studio) && (
                  <p className="flex items-center gap-2 text-white/80">
                    <MapPin className="w-4 h-4" />
                    {formatStudioAddress(studio)}
                  </p>
                )}
              </div>
              
              <div className="flex gap-2">
                {isAuthenticated && (
                  <Button 
                    variant="outline" 
                    className="border-white/30 text-white hover:bg-white/20"
                    onClick={() => setShowReviewDialog(true)}
                    data-testid="write-review-btn"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Review
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/20 w-fit"
                  onClick={handleFavourite}
                  data-testid="favourite-studio-btn"
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavourite ? 'fill-rose-500 text-rose-500' : ''}`} />
                  {isFavourite ? 'Saved' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Policy Banner */}
      {studio.cancellation_notice_hours && (
        <div className="bg-amber-50 border-b border-amber-200 py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-amber-800">
            <AlertCircle className="w-4 h-4" />
            <span>
              Cancellation Policy: {studio.cancellation_notice_hours}h notice required for refund. 
              {studio.cancellation_refund_type === 'class_pass_credit' && ' Credits returned to ClassPass.'}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue={initialTab} className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="about" data-testid="tab-about">About</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="classpasses" data-testid="tab-classpasses">Class Passes</TabsTrigger>
            <TabsTrigger value="instructors" data-testid="tab-instructors">Instructors</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({reviews.total_reviews})</TabsTrigger>
          </TabsList>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            {/* User's Active ClassPasses Banner */}
            {userClassPasses.length > 0 && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Ticket className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-emerald-900">You have {userClassPasses.length} active ClassPass(es)</p>
                      <p className="text-sm text-emerald-700">
                        {userClassPasses.map(p => `${p.class_pass_name}: ${p.passes_remaining} passes`).join(' • ')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Select Date</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-md border"
                    data-testid="class-calendar"
                  />
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    Classes this week
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMM d')}
                    {' – '}
                    {format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMM d, yyyy')}
                  </p>
                </div>

                {/* Iter-46an: day-of-week chips — click to jump to the day
                    within the visible week (sets selectedDate so the calendar
                    + day-section header stay in sync). */}
                {(() => {
                  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
                  const dayChips = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
                  return (
                    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="day-chips">
                      {dayChips.map((day) => {
                        const isActive = isSameDay(day, selectedDate);
                        const dayCount = classes.filter((cls) =>
                          isSameDay(parseISO(cls.start_time), day)
                        ).length;
                        return (
                          <button
                            key={day.toISOString()}
                            type="button"
                            onClick={() => setSelectedDate(day)}
                            data-testid={`day-chip-${format(day, 'EEE').toLowerCase()}`}
                            className={`flex flex-col items-center justify-center min-w-[64px] px-3 py-2 rounded-full border text-xs font-medium transition-colors ${
                              isActive
                                ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                : 'bg-background hover:bg-muted border-border'
                            }`}
                          >
                            <span className="uppercase tracking-wide">{format(day, 'EEE')}</span>
                            <span className={`text-base leading-tight font-semibold ${isActive ? '' : 'text-foreground'}`}>
                              {format(day, 'd')}
                            </span>
                            <span className={`text-[10px] mt-0.5 ${isActive ? 'opacity-90' : 'text-muted-foreground'}`}>
                              {dayCount === 0 ? '—' : `${dayCount} ${dayCount === 1 ? 'class' : 'classes'}`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {classes.length > 0 ? (
                  (() => {
                    // Iter-46an: group fetched classes by day-of-week so each
                    // day gets its own header + class list. The day that
                    // matches the calendar's selected date is highlighted.
                    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
                    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
                    const byDay = days.map((day) => ({
                      day,
                      classes: classes.filter((cls) =>
                        isSameDay(parseISO(cls.start_time), day)
                      ),
                    }));
                    let globalIndex = 0;
                    return (
                      <div className="space-y-5" data-testid="week-classes-panel">
                        {byDay.map(({ day, classes: dayClasses }) => {
                          const isSelected = isSameDay(day, selectedDate);
                          return (
                            <div key={day.toISOString()} className="space-y-2">
                              <div
                                className={`flex items-baseline justify-between border-b pb-1 ${
                                  isSelected ? 'border-primary text-primary' : 'border-border'
                                }`}
                              >
                                <h4 className={`font-semibold ${isSelected ? 'text-primary' : ''}`}>
                                  {format(day, 'EEEE')}
                                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                                    {format(day, 'MMM d')}
                                  </span>
                                </h4>
                                <span className="text-xs text-muted-foreground">
                                  {dayClasses.length === 0
                                    ? 'No classes'
                                    : `${dayClasses.length} class${dayClasses.length === 1 ? '' : 'es'}`}
                                </span>
                              </div>
                              {dayClasses.length === 0 ? null : dayClasses.map((cls) => {
                                const isFull = cls.current_bookings >= cls.max_capacity;
                                const isBooked = !!cls.is_booked;
                                const i = globalIndex++;
                                return (
                                  <Card key={cls.id} className="hover:shadow-md transition-shadow" data-testid={`class-card-${i}`}>
                                    <CardContent className="p-4">
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <Badge variant="outline">{formatActivity(cls.activity_type || cls.category) || 'Class'}</Badge>
                                            <h4 className="font-semibold">{cls.name}</h4>
                                            {isBooked && (
                                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                                Booked
                                              </Badge>
                                            )}
                                            {!isBooked && isFull && (
                                              <Badge variant="destructive" className="text-xs">Full</Badge>
                                            )}
                                            {cls.waitlist_count > 0 && (
                                              <Badge variant="secondary" className="text-xs">
                                                {cls.waitlist_count} on waitlist
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-4 h-4" />
                                              {format(new Date(cls.start_time), 'h:mm a')} - {format(new Date(cls.end_time), 'h:mm a')}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <Users className="w-4 h-4" />
                                              {cls.current_bookings}/{cls.max_capacity}
                                            </span>
                                            {cls.instructor_name && (
                                              <span>with {cls.instructor_name}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-right flex flex-col gap-2">
                                          {cls.price > 0 && (
                                            <p className="font-semibold text-primary">€{cls.price}</p>
                                          )}
                                          {isBooked ? (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              disabled
                                              data-testid={`already-booked-btn-${i}`}
                                            >
                                              Already Booked
                                            </Button>
                                          ) : !isFull ? (
                                            <Button
                                              size="sm"
                                              onClick={() => handleBookClass({ ...cls, studio_id: studio.id, studio_name: studio.name, studio_slug: studio.slug })}
                                              data-testid={`book-class-btn-${i}`}
                                            >
                                              Book
                                            </Button>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => handleJoinWaitlist(cls)}
                                              data-testid={`waitlist-btn-${i}`}
                                            >
                                              <Bell className="w-4 h-4 mr-1" />
                                              Join Waitlist
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : (
                  <Card className="p-8 text-center">
                    <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No classes scheduled for this week</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ClassPasses Tab */}
          <TabsContent value="classpasses" className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">What is a ClassPass?</h3>
              <p className="text-muted-foreground">
                ClassPasses let you purchase a set number of classes for a specific activity type. 
                They&apos;re perfect if you want flexibility without a full membership commitment.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {classPasses.length > 0 ? (
                classPasses.map((pass, i) => (
                  <Card key={pass.id} className="relative overflow-hidden" data-testid={`classpass-card-${i}`}>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-orange-400" />
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-1">
                        <Ticket className="w-5 h-5 text-primary" />
                        <Badge variant="outline">{formatActivity(pass.activity_type)}</Badge>
                      </div>
                      <CardTitle>{pass.name}</CardTitle>
                      <CardDescription>{pass.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <span className="text-3xl font-bold">€{pass.price}</span>
                        <span className="text-muted-foreground"> / {pass.passes_count} classes</span>
                      </div>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {pass.passes_count} classes for {formatActivity(pass.activity_type)}
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          {/* Bug R16 fix (Iter-65): both validity_days===0
                              and legacy values >= 5 years should render as
                              "Never expires" — the 36,500-day sentinel used
                              to leak through to customers. */}
                          {(pass.validity_days === 0 || pass.validity_days >= 1825)
                            ? 'Never expires'
                            : `Valid for ${pass.validity_days} days`}
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-500" />
                          Refundable with {studio.cancellation_notice_hours || 24}h notice
                        </li>
                      </ul>
                      {!studioCanAcceptPayments && pass.price > 0 ? (
                        <div className="space-y-2">
                          <Button 
                            className="w-full"
                            disabled
                            variant="secondary"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Payments Not Available
                          </Button>
                          <p className="text-xs text-amber-600 text-center">
                            {paymentStatusMessage || 'This studio has not configured payment processing yet.'}
                          </p>
                        </div>
                      ) : (
                        <Button 
                          className="w-full"
                          onClick={() => setPurchasePass({ ...pass, studio_id: studio.id, studio_name: studio.name })}
                          data-testid={`purchase-classpass-btn-${i}`}
                        >
                          <Ticket className="w-4 h-4 mr-2" />
                          Purchase ClassPass
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full p-8 text-center">
                  <Ticket className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No ClassPasses available yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Instructors Tab */}
          <TabsContent value="instructors" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {instructors.length > 0 ? (
                instructors.map((instructor, i) => (
                  <Card key={instructor.id} data-testid={`instructor-card-${i}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <img 
                          src={instructor.photo_url || 'https://images.unsplash.com/photo-1750698545009-679820502908?w=100&h=100&fit=crop'}
                          alt={instructor.first_name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div>
                          <h4 className="font-semibold">{instructor.first_name} {instructor.last_name}</h4>
                          <p className="text-sm text-muted-foreground mb-2">{instructor.email}</p>
                          {instructor.specialties?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {instructor.specialties.map((s, j) => (
                                <Badge key={j} variant="secondary" className="text-xs">{s}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {instructor.bio && (
                        <p className="mt-4 text-sm text-muted-foreground">{instructor.bio}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No instructors listed yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>About {studio.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  {studio.description || 'A premier fitness studio offering world-class training and facilities.'}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 pt-4">
                  {formatStudioAddress(studio) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">Address</p>
                        <p className="text-sm text-muted-foreground">{formatStudioAddress(studio)}</p>
                      </div>
                    </div>
                  )}
                  {studio.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <p className="text-sm text-muted-foreground">{studio.phone}</p>
                      </div>
                    </div>
                  )}
                  {studio.email && (
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium">Email</p>
                        <p className="text-sm text-muted-foreground">{studio.email}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Iter-46an: location map + directions link */}
                {formatStudioAddress(studio) && (
                  <div className="pt-4 border-t" data-testid="studio-location-section">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Location
                      </h4>
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        data-testid="get-directions-btn"
                      >
                        <a
                          href={googleMapsDirectionsUrl(studio)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Get Directions
                        </a>
                      </Button>
                    </div>
                    <div className="rounded-lg overflow-hidden border" data-testid="studio-map">
                      <iframe
                        title={`Map showing ${studio.name}`}
                        width="100%"
                        height="300"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(formatStudioAddress(studio))}&output=embed`}
                        style={{ border: 0 }}
                      />
                    </div>
                  </div>
                )}

                {/* Cancellation Policy Section */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Cancellation Policy
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Minimum notice required: {studio.cancellation_notice_hours || 24} hours</li>
                    <li>• Refund type: {studio.cancellation_refund_type === 'class_pass_credit' ? 'ClassPass credit returned' : 
                      studio.cancellation_refund_type === 'full_refund' ? 'Full refund' : 'No refund'}</li>
                    {studio.late_cancel_penalty > 0 && (
                      <li>• Late cancellation penalty: {studio.late_cancel_penalty}%</li>
                    )}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Reviews</CardTitle>
                  <CardDescription>What clients are saying about {studio.name}</CardDescription>
                </div>
                {isAuthenticated && (
                  <Button onClick={() => setShowReviewDialog(true)} data-testid="write-review-btn-tab">
                    <Star className="w-4 h-4 mr-2" />
                    Write Review
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Rating Summary */}
                <div className="flex items-center gap-6 p-4 bg-muted rounded-lg mb-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold">{reviews.average_rating || '-'}</p>
                    <div className="flex gap-0.5 justify-center my-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-4 h-4 ${star <= reviews.average_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">{reviews.total_reviews} reviews</p>
                  </div>
                </div>

                {/* Reviews List */}
                {reviews.reviews && reviews.reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.reviews.map((review, i) => (
                      <div key={review.id} className="p-4 border rounded-lg" data-testid={`review-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{review.user_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(review.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div className="flex gap-0.5 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        {/* Iter-54: render the review comment. Prior template
                            omitted it entirely, so users only ever saw the
                            star rating regardless of what was written. */}
                        {review.comment && (
                          <p
                            className="text-sm text-foreground/90 whitespace-pre-line"
                            data-testid={`review-${i}-comment`}
                          >
                            {review.comment}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No reviews yet</p>
                    {isAuthenticated && (
                      <p className="text-sm text-muted-foreground mt-2">Be the first to leave a review!</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Dialog — shared with SchedulePage via <ClassBookingDialog /> */}
      <ClassBookingDialog
        bookingClass={bookingClass}
        onClose={() => setBookingClass(null)}
        onBooked={handleBooked}
      />

      {/* ClassPass Purchase Dialog — shared <ClassPassPurchaseDialog /> handles
          voucher entry, RyftPay 3DS flow, and saved-card chips inside the SDK form. */}
      <ClassPassPurchaseDialog
        pass={purchasePass}
        studio={studio}
        onClose={() => setPurchasePass(null)}
        onPurchased={() => {
          setPurchasePass(null);
          fetchUserClassPasses();
          fetchSavedCards();
        }}
      />

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Write a Review</DialogTitle>
            <DialogDescription>Share your experience at {studio?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Rating</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= reviewRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Review (Optional)</label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Tell others about your experience..."
                className="w-full p-3 rounded-lg border min-h-24 resize-none"
                data-testid="review-comment-input"
              />
              <p className="text-xs text-muted-foreground">
                Your review will be shared with the studio owner. Only your rating is visible publicly.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReviewDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={reviewLoading} data-testid="submit-review-btn">
                {reviewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Review'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Card for Purchase Dialog */}
    </div>
  );
};
